// The posters beside the sign-in and set-up forms on a wide screen: who the app is for,
// then four things it does, each with a small drawn piece of it. They change only by hand (no timer), and the
// pictures are drawn from the theme's tokens, so there is nothing to download and nothing
// to keep in step with a screenshot.
import { Badge, IconTile, cx } from '@ims/ui'
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CloudOff,
  Gift,
  Package,
  Pill,
  Receipt,
  ShoppingBasket,
  Store,
  UtensilsCrossed,
} from '@ims/ui/icons'
import { useState, type ReactNode } from 'react'
import { BUSINESS_TYPES } from '../lib/catalog.ts'

const PAPER = 'rounded-card bg-surface text-ink shadow-modal'

function BillLine({ name, qty, amount }: { name: string; qty: string; amount: string }) {
  return (
    <div className="flex gap-2.5 border-b border-soft py-1.5 text-[13px]">
      <span className="flex-1">{name}</span>
      <span className="tabular-nums text-muted">{qty}</span>
      <span className="w-[74px] text-right font-semibold tabular-nums">{amount}</span>
    </div>
  )
}

function Total({ label, amount, big }: { label: string; amount: string; big?: boolean }) {
  return (
    <div
      className={cx(
        'flex justify-between tabular-nums',
        big ? 'pt-2 text-base font-extrabold' : 'pt-1.5 text-[13px] text-muted',
      )}
    >
      <span>{label}</span>
      <span>{amount}</span>
    </div>
  )
}

function OfflineArt() {
  return (
    <div className="relative w-full max-w-[420px]">
      <div className={cx(PAPER, 'px-5 py-4')}>
        <div className="mb-2 flex items-center justify-between">
          <b className="text-[14.5px]">Bill INV-WEB-2083-00412</b>
          <Badge tone="warning" className="gap-1 font-bold">
            <CloudOff className="size-3.5" />
            Offline
          </Badge>
        </div>
        <BillLine name="Basmati rice 5 kg" qty="2" amount="1,900.00" />
        <BillLine name="Sunflower oil 1 L" qty="3" amount="960.00" />
        <BillLine name="Wai Wai noodles" qty="12" amount="300.00" />
        <Total label="Total" amount="Rs. 3,160.00" big />
      </div>
      <div
        className={cx(
          PAPER,
          'absolute -bottom-11 -right-5 flex items-center gap-2.5 px-3.5 py-2.5',
        )}
      >
        <span className="flex size-[30px] items-center justify-center rounded-lg bg-success-light text-success">
          <CheckCircle2 className="size-[18px]" />
        </span>
        <span className="text-xs leading-tight">
          <b className="block text-[13px]">Saved on this device</b>
          <span className="text-muted">Goes up when the internet is back</span>
        </span>
      </div>
    </div>
  )
}

function BillArt() {
  return (
    <div className={cx(PAPER, 'w-full max-w-[420px] px-5 py-4')}>
      <div className="mb-2.5 flex items-start justify-between">
        <span>
          <b className="block text-[14.5px]">Himalaya Kirana</b>
          <span className="text-xs text-muted">VAT No. 301234567</span>
        </span>
        <span className="text-right text-xs text-muted">
          <b className="block text-[13px] text-ink">26 Asoj 2083</b>
          Tax invoice
        </span>
      </div>
      <BillLine name="Basmati rice 5 kg" qty="2" amount="1,900.00" />
      <BillLine name="Sunflower oil 1 L" qty="3" amount="960.00" />
      <Total label="Taxable amount" amount="2,860.00" />
      <Total label="VAT 13%" amount="371.80" />
      <Total label="Total" amount="Rs. 3,231.80" big />
    </div>
  )
}

function StockRow({
  name,
  sub,
  tag,
  tone,
}: {
  name: string
  sub: string
  tag: string
  tone: 'danger' | 'warning'
}) {
  return (
    <div className="flex items-center gap-2.5 border-b border-soft py-2 last:border-0">
      <IconTile area="stock">
        <Package className="size-[17px]" />
      </IconTile>
      <span className="min-w-0 flex-1">
        <b className="block text-[13.5px]">{name}</b>
        <span className="text-xs text-muted">{sub}</span>
      </span>
      <Badge tone={tone} className="whitespace-nowrap font-bold">
        {tag}
      </Badge>
    </div>
  )
}

function StockArt() {
  return (
    <div className={cx(PAPER, 'w-full max-w-[420px] px-5 pb-2 pt-3.5')}>
      <div className="text-section mb-0.5 text-subtle">Needs your attention</div>
      <StockRow name="Sunflower oil 1 L" sub="Reorder at 12" tag="4 left" tone="danger" />
      <StockRow name="Wai Wai noodles" sub="Reorder at 60" tag="22 left" tone="warning" />
      <StockRow
        name="Amul butter 500 g"
        sub="Batch B-0931"
        tag="Expires in 9 days"
        tone="warning"
      />
    </div>
  )
}

function TrialArt() {
  return (
    <div className={cx(PAPER, 'flex w-full max-w-[420px] items-center gap-5 px-5 py-5')}>
      <div className="shrink-0 text-center">
        <div className="text-[62px] font-extrabold leading-none tracking-tighter text-primary">
          90
        </div>
        <div className="text-[13px] font-bold text-muted">days free</div>
      </div>
      <ul className="space-y-2 border-l border-border pl-5 text-[13.5px] font-semibold">
        {['A main store, ready', 'A billing counter, ready', 'Nothing to pay to start'].map((t) => (
          <li key={t} className="flex items-center gap-2">
            <CheckCircle2 className="size-[18px] shrink-0 text-success" />
            {t}
          </li>
        ))}
      </ul>
    </div>
  )
}

function KindCard({
  area,
  icon,
  name,
  text,
  wide,
}: {
  area: 'stock' | 'kitchen' | 'take'
  icon: ReactNode
  name: string
  text: string
  wide?: boolean
}) {
  return (
    <div className={cx(PAPER, 'flex gap-3 p-4', wide ? 'items-center' : 'flex-col')}>
      <IconTile area={area}>{icon}</IconTile>
      <span className="min-w-0">
        <b className="block text-[14.5px] leading-snug">{name}</b>
        <span className="mt-0.5 block text-xs leading-snug text-muted">{text}</span>
      </span>
    </div>
  )
}

// The three kinds the poster leads with; every other kind in the set-up list is a chip.
const LEAD_KINDS = ['kirana', 'restaurant', 'pharmacy']
const KIND_CHIP =
  'h-[26px] items-center whitespace-nowrap rounded-full border border-on-fill/35 bg-on-fill/10 px-2.5 text-xs font-semibold'
// A short window (a small laptop) has room for one row of chips. The classes are spelt out
// whole so Tailwind finds them.
const CHIP_TALL_ONLY = '[@media(max-height:800px)]:hidden'
const CHIP_SHORT_ONLY = 'hidden [@media(max-height:800px)]:inline-flex'

function KindsArt() {
  const others = BUSINESS_TYPES.filter((t) => !LEAD_KINDS.includes(t.id))
  return (
    <div className="grid w-full max-w-[420px] gap-2.5">
      <KindCard
        wide
        area="stock"
        icon={<ShoppingBasket className="size-5" />}
        name="Kirana, grocery and marts"
        text="Quick bills at the counter, packs and loose items, stock by item"
      />
      <div className="grid grid-cols-2 gap-2.5">
        <KindCard
          area="kitchen"
          icon={<UtensilsCrossed className="size-5" />}
          name="Restaurants and cafés"
          text="Tables, kitchen tickets, a QR menu"
        />
        <KindCard
          area="take"
          icon={<Pill className="size-5" />}
          name="Pharmacies"
          text="Batches and expiry dates on every medicine"
        />
      </div>
      <div className="mt-2">
        <div className="text-section mb-1.5 text-on-fill/80">Also for</div>
        <ul className="flex flex-wrap gap-1.5">
          {others.map((t, i) => (
            <li key={t.id} className={cx(KIND_CHIP, 'inline-flex', i > 1 && CHIP_TALL_ONLY)}>
              {t.label}
            </li>
          ))}
          <li className={cx(KIND_CHIP, CHIP_SHORT_ONLY)}>and more</li>
        </ul>
      </div>
    </div>
  )
}

export const POSTER_SLIDES: {
  id: 'kinds' | 'offline' | 'bills' | 'stock' | 'trial'
  tag: string
  icon: ReactNode
  title: string
  text: string
  art: ReactNode
}[] = [
  {
    id: 'kinds',
    tag: 'For your shop',
    icon: <Store className="size-[15px]" />,
    title: 'Made for your kind of shop',
    text: 'Pick your business at set-up and the app shows only what that shop needs.',
    art: <KindsArt />,
  },
  {
    id: 'offline',
    tag: 'Works offline',
    icon: <CloudOff className="size-[15px]" />,
    title: 'Keeps billing when the internet drops',
    text: 'Every bill is saved on your own device first, and sent up when you are back online.',
    art: <OfflineArt />,
  },
  {
    id: 'bills',
    tag: 'Made for Nepal',
    icon: <Receipt className="size-[15px]" />,
    title: 'Bills the way Nepal needs them',
    text: 'Bikram Sambat dates, PAN or VAT bills, and totals in rupees and paisa.',
    art: <BillArt />,
  },
  {
    id: 'stock',
    tag: 'Stock',
    icon: <Package className="size-[15px]" />,
    title: 'Know what is running low',
    text: 'Stock goes down with every bill, and the app tells you what to reorder and what is about to expire.',
    art: <StockArt />,
  },
  {
    id: 'trial',
    tag: 'Free trial',
    icon: <Gift className="size-[15px]" />,
    title: 'Free for 90 days',
    text: 'A main store and a billing counter are ready the moment you finish. Nothing to pay to start.',
    art: <TrialArt />,
  },
]

const DOTS = {
  backgroundImage:
    'radial-gradient(circle at 1px 1px, color-mix(in srgb, var(--color-on-fill) 10%, transparent) 1px, transparent 0)',
  backgroundSize: '22px 22px',
}
const ARROW =
  'flex size-9 items-center justify-center rounded-full border border-on-fill/35 hover:bg-on-fill/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-on-fill/60'

export function AuthPoster({
  start = 'kinds',
  className,
}: {
  start?: (typeof POSTER_SLIDES)[number]['id']
  className?: string
}) {
  const count = POSTER_SLIDES.length
  const [at, setAt] = useState(() =>
    Math.max(
      0,
      POSTER_SLIDES.findIndex((s) => s.id === start),
    ),
  )
  const slide = POSTER_SLIDES[at]!
  return (
    <aside
      aria-label="What Sano Godam does"
      style={DOTS}
      className={cx(
        'min-w-0 flex-1 flex-col overflow-hidden rounded-3xl bg-primary px-12 pb-8 pt-11 text-on-fill',
        className,
      )}
    >
      {/* Keyed, so a new slide fades in; the fade stops under reduced motion. */}
      <div key={slide.id} aria-live="polite" className="flex flex-1 animate-fade-in flex-col">
        <div>
          <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-on-fill/15 px-3 text-xs font-bold">
            {slide.icon}
            {slide.tag}
          </span>
        </div>
        <h2 className="mb-2 mt-4 max-w-[440px] text-[32px] font-extrabold leading-[1.15] tracking-tight">
          {slide.title}
        </h2>
        <p className="max-w-[420px] text-[15.5px] leading-normal text-on-fill/90">{slide.text}</p>
        <div
          className={cx(
            'flex flex-1 items-center justify-center px-6',
            // The tallest poster: less air round it, so the arrows stay in view.
            slide.id === 'kinds' ? 'py-6' : 'py-14',
          )}
        >
          {slide.art}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="flex items-center gap-1.5" aria-hidden>
          {POSTER_SLIDES.map((s, i) => (
            <span
              key={s.id}
              className={cx(
                'h-[7px] rounded-full',
                i === at ? 'w-[22px] bg-on-fill' : 'w-[7px] bg-on-fill/40',
              )}
            />
          ))}
        </span>
        <span className="text-xs text-on-fill/80">
          {at + 1} of {count}
        </span>
        <span className="ml-auto flex gap-2">
          <button
            type="button"
            aria-label="Previous"
            className={ARROW}
            onClick={() => setAt((at + count - 1) % count)}
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Next"
            className={ARROW}
            onClick={() => setAt((at + 1) % count)}
          >
            <ChevronRight className="size-4" />
          </button>
        </span>
      </div>
    </aside>
  )
}
